import React, { useState } from 'react';
import { Form, useNavigate, json, redirect, useNavigation, useSubmit } from "react-router-dom";
import ConfirmModal from "../../UI/ConfirmModal";
import { useRouteLoaderData } from "react-router-dom";
import classes from './EditPatient.module.css';
import { getAuthToken } from "../../../util/auth";
import { getApiChara } from "../../../util/getApi";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaUserEdit, FaIdCard, FaAddressCard, FaPhoneAlt, FaMapMarkerAlt, FaSave, FaTimes } from 'react-icons/fa';
import { useTranslation } from "react-i18next";
import { calculateAgeDetailed } from "../../../util/ageCalculator";

function formatDateString(dateString) {
    if (!dateString || dateString === null || dateString === "null") return "";
    const dateObject = new Date(dateString);
    if (isNaN(dateObject.getTime())) return "";
    const year = dateObject.getFullYear();
    const month = String(dateObject.getMonth() + 1).padStart(2, "0");
    const day = String(dateObject.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

const calculateAge = (dobString) => {
    const { years } = calculateAgeDetailed(dobString);
    return years;
};

const EditPatient = () => {
    const { t } = useTranslation();
    const PatientDetails = useRouteLoaderData('Patient-Info');
    const navigate = useNavigate();
    const navigation = useNavigation();
    const isSubmitting = navigation.state === 'submitting';
    const formattedDate = PatientDetails ? formatDateString(PatientDetails?.dob) : "";

    const initialAge = PatientDetails?.age !== undefined && PatientDetails?.age !== null ? PatientDetails.age : (PatientDetails ? calculateAge(PatientDetails?.dob) : "");
    const [age, setAge] = useState(initialAge);
    const [ageText, setAgeText] = useState(PatientDetails ? calculateAgeDetailed(PatientDetails?.dob).text : "");

    const dobChangeHandler = (event) => {
        const newDob = event.target.value;
        const { years, text } = calculateAgeDetailed(newDob);
        setAge(years);
        setAgeText(text);
    };

    const submit = useSubmit();
    const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);
    const [pendingForm, setPendingForm] = useState(null);

    const handleSubmit = (event) => {
        event.preventDefault();
        setPendingForm(event.currentTarget);
        setShowUpdateConfirm(true);
    };

    const confirmUpdate = () => {
        if (pendingForm) {
            submit(pendingForm);
        }
        setShowUpdateConfirm(false);
    };

    function cancelHandler() {
        navigate("..");
    }

    return (
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h3 className='textHead' style={{ color: 'var(--primary-color)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px' }}>
                <FaUserEdit className="me-2" /> {t("Edit Patient Details")}
            </h3>
            <div className={classes.cardview}>
                <Form method='put' className={classes.form} onSubmit={handleSubmit}>

                    <div className={classes.sectionHeader}>
                        <FaIdCard /> {t("Personal Information")}
                    </div>

                    <div className="row">
                        <div className="col-md-4 col-6">
                            <label className={classes.label} htmlFor="firstName">{t("First Name")}</label>
                            <input id="firstName" type="text" name="firstName" required defaultValue={PatientDetails ? PatientDetails?.firstName : ''} />
                            <input id="patientId" type="hidden" name="patientId" required defaultValue={PatientDetails ? PatientDetails?.patientId : ''} />
                        </div>
                        <div className="col-md-4 col-6">
                            <label className={classes.label} htmlFor="middleName">{t("Middle Name")}</label>
                            <input id="middleName" type="text" name="middleName" defaultValue={PatientDetails ? PatientDetails?.middleName : ''} />
                        </div>
                        <div className="col-md-4 col-6">
                            <label className={classes.label} htmlFor="lastName">{t("Last Name")}</label>
                            <input id="lastName" type="text" name="lastName" defaultValue={PatientDetails ? PatientDetails?.lastName : ''} />
                        </div>
                        <div className="col-md-4 col-6">
                            <label className={classes.label} htmlFor="nickName">{t("Nick Name")}</label>
                            <input id="nickName" type="text" name="nickName" defaultValue={PatientDetails ? PatientDetails?.nickName : ''} />
                        </div>
                        <div className="col-md-4 col-6">
                            <label className={classes.label} htmlFor="gender">{t("Gender")}</label>
                            <select id="gender" name="gender" defaultValue={PatientDetails ? PatientDetails?.gender : ''} required>
                                <option value="male">{t("Male")}</option>
                                <option value="female">{t("Female")}</option>
                                <option value="other">{t("Other")}</option>
                            </select>
                        </div>
                        <div className="col-md-4 col-6">
                            <label className={classes.label} htmlFor="dob">{t("Date of Birth")}</label>
                            <input id="dob" type="date" name="dob" defaultValue={formattedDate} onChange={dobChangeHandler} />
                        </div>
                        <div className="col-md-4 col-6">
                            <label className={classes.label} htmlFor="age">{t("Age (Years)")}</label>
                            <div className="d-flex align-items-center gap-2">
                                <input id="age" type="number" name="age" value={age} onChange={(e) => setAge(e.target.value)} style={{ flex: 1 }} />
                                {ageText && <span className="text-primary fw-bold small" style={{ whiteSpace: 'nowrap' }}>{ageText}</span>}
                            </div>
                        </div>
                        <div className="col-md-4 col-6">
                            <label className={classes.label} htmlFor="maritalStatus">{t("Marital Status")}</label>
                            <select id="maritalStatus" name="maritalStatus" defaultValue={PatientDetails ? PatientDetails?.maritalStatus : ''}>
                                <option value="single">{t("Single")}</option>
                                <option value="married">{t("Married")}</option>
                                <option value="divorced">{t("Divorced")}</option>
                                <option value="widowed">{t("Widowed")}</option>
                            </select>
                        </div>
                    </div>

                    <div className={classes.sectionHeader}>
                        <FaAddressCard /> {t("Identity & Background")}
                    </div>
                    <div className="row">
                        <div className="col-md-4 col-6">
                            <label className={classes.label} htmlFor="religion">{t("Religion")}</label>
                            <input id="religion" type="text" name="religion" defaultValue={PatientDetails ? PatientDetails?.religion : ''} />
                        </div>
                        <div className="col-md-4 col-6">
                            <label className={classes.label} htmlFor="caste">{t("Caste")}</label>
                            <input id="caste" type="text" name="caste" defaultValue={PatientDetails ? PatientDetails?.caste : ''} />
                        </div>
                        <div className="col-md-4 col-6">
                            <label className={classes.label} htmlFor="occupation">{t("Occupation")}</label>
                            <input id="occupation" type="text" name="occupation" defaultValue={PatientDetails ? PatientDetails?.occupation : ''} />
                        </div>
                        <div className="col-md-4 col-6">
                            <label className={classes.label} htmlFor="panCardNo">{t("Pan Card No")}</label>
                            <input id="panCardNo" type="text" name="panCardNo" defaultValue={PatientDetails ? PatientDetails?.panCardNo : ''} />
                        </div>
                        <div className="col-md-4 col-12">
                            <label className={classes.label} htmlFor="adharCardNo">{t("Aadhar Card No")}</label>
                            <input id="adharCardNo" type="text" name="adharCardNo" defaultValue={PatientDetails ? PatientDetails?.adharCardNo : ''} />
                        </div>
                        <div className="col-md-4 col-6">
                            <label className={classes.label} htmlFor="identificationMark">{t("Identification Mark")}</label>
                            <input id="identificationMark" type="text" name="identificationMark" defaultValue={PatientDetails ? PatientDetails?.identificationMark : ''} />
                        </div>
                    </div>

                    <div className={classes.sectionHeader}>
                        <FaPhoneAlt /> {t("Contact Information")}
                    </div>
                    <div className="row">
                        <div className="col-md-4 col-6">
                            <label className={classes.label} htmlFor="mobile">{t("Mobile Number")}</label>
                            <input id="mobile" type="tel" name="mobile" defaultValue={PatientDetails ? PatientDetails?.mobile : ''} />
                        </div>
                        <div className="col-md-4 col-6">
                            <label className={classes.label} htmlFor="alternateMobileNum">{t("Alt Mobile No")}</label>
                            <input id="alternateMobileNum" type="tel" name="alternateMobileNum" defaultValue={PatientDetails ? PatientDetails?.alternateMobileNum : ''} />
                        </div>
                        <div className="col-md-4 col-6">
                            <label className={classes.label} htmlFor="emailId">{t("Email")}</label>
                            <input id="emailId" type="email" name="emailId" defaultValue={PatientDetails ? PatientDetails?.emailId : ''} />
                        </div>
                    </div>

                    <div className={classes.sectionHeader}>
                        <FaMapMarkerAlt /> {t("Address & Reference")}
                    </div>
                    <div className="row">
                        <div className="col-md-4 col-6">
                            <label className={classes.label} htmlFor="address">{t("Address")}</label>
                            <input id="address" type="text" name="address" defaultValue={PatientDetails ? PatientDetails?.address : ''} />
                        </div>
                        <div className="col-md-4 col-6">
                            <label className={classes.label} htmlFor="city">{t("City")}</label>
                            <input id="city" type="text" name="city" defaultValue={PatientDetails ? PatientDetails?.city : ''} />
                        </div>
                        <div className="col-md-4 col-6">
                            <label className={classes.label} htmlFor="pincode">{t("Pincode")}</label>
                            <input id="pincode" type="text" name="pincode" defaultValue={PatientDetails ? PatientDetails?.pincode : ''} />
                        </div>
                        <div className="col-md-4 col-12">
                            <label className={classes.label} htmlFor="referredBy">{t("Referred By")}</label>
                            <input id="referredBy" type="text" name="referredBy" defaultValue={PatientDetails ? PatientDetails?.referredBy : ''} />
                        </div>
                        <div className="col-md-4 col-12">
                            <label className={classes.label} htmlFor="registrationNo">{t("Registration No")}</label>
                            <input id="registrationNo" type="text" name="registrationNo" defaultValue={PatientDetails ? PatientDetails?.registrationNo : ''} />
                        </div>
                    </div>

                    <div className={classes.actions}>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                        >
                            <FaSave className="me-2" /> {isSubmitting ? t("Saving...") : t('Save Changes')}
                        </button>
                        <button
                            type="button"
                            onClick={cancelHandler}
                            className={classes.secondaryBtn}
                        >
                            <FaTimes className="me-2" /> {t("Cancel")}
                        </button>
                    </div>
                </Form>
            </div>
            <ConfirmModal
                show={showUpdateConfirm}
                onHide={() => setShowUpdateConfirm(false)}
                onConfirm={confirmUpdate}
                title={t("Update Patient")}
                message={t("Are you sure you want to save the changes to this patient's information?")}
                variant="update"
                confirmText={t("Update")}
            />
        </div>
    );
}
export default EditPatient;


export async function action({ request }) {
    const data = await request.formData();
    const patientData = {
        patientId: data.get('patientId'),
        firstName: data.get('firstName'),
        middleName: data.get('middleName'),
        lastName: data.get('lastName'),
        nickName: data.get('nickName'),
        dob: data.get('dob') || null,
        maritalStatus: data.get('maritalStatus'),
        religion: data.get('religion'),
        caste: data.get('caste'),
        panCardNo: data.get('panCardNo'),
        adharCardNo: data.get('adharCardNo'),
        gender: data.get('gender'),
        age: data.get('age'),
        occupation: data.get('occupation'),
        identificationMark: data.get('identificationMark'),
        emailId: data.get('emailId'),
        mobile: data.get('mobile'),
        alternateMobileNum: data.get('alternateMobileNum'),
        address: data.get('address'),
        city: data.get('city'),
        pincode: data.get('pincode'),
        referredBy: data.get('referredBy'),
        registrationNo: data.get('registrationNo'),
    }
    const token = getAuthToken();
    if (token === null || token === 'EXPIRED') {
        return redirect('/');
    }
    const apiChar = getApiChara();
    const apiUrl = `${apiChar}/patient/updatePatient`;

    try {
        const submissionData = {
            ...patientData,
            dob: patientData.dob || null
        };
        const response = await fetch(apiUrl, {
            method: "PUT",
            body: JSON.stringify(submissionData),
            headers: {
                'Content-Type': 'application/json',
                'authorization': 'Bearer ' + token
            }
        });
        if (!response.ok) {
            toast.error("Could not update Patient");
            throw json({ message: 'Could not updated Patient.' }, { status: 500 });
        } else {
            const data = await response.json();
            toast.success("Patient updated successfully!");
            return redirect('..');
        }
    }
    catch (error) {
        console.error('There has been a problem with your fetch operation:', error);
    }
}